import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, BrowserRouter, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';

// Auth components
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import PermissionRoute from './components/auth/PermissionRoute';
import { AuthProvider } from './contexts/AuthContext';
import { MenuPermissionsProvider } from './contexts/MenuPermissionsContext';

// Layout components
import AdminLayout from './components/layout/AdminLayout';

// Dashboard components
import Dashboard from './components/dashboard/Dashboard';
import MyDashboard from './components/dashboard/MyDashboard';

// Client components
import ClientList from './components/clients/ClientList';
import ClientDetails from './components/clients/ClientDetails';
import AddClient from './components/clients/AddClient';

// Vendor components
import Vendors from './components/vendors/Vendors';
import VendorDetail from './components/vendors/VendorDetail';
import AddVendor from './components/vendors/AddVendor';
import EditVendor from './components/vendors/EditVendor';

// Project components
import ProjectList from './components/projects/ProjectList';
import ProjectDetails from './components/projects/ProjectDetails';
import AddProject from './components/projects/AddProject';
import TemplateForm from './components/projects/TemplateForm';

// Phase components
import PhaseDetails from './components/phases/PhaseDetails';

// Step components
import StepDetails from './components/steps/StepDetails';

// Task components
import TasksPage from './components/tasks/TasksPage';

// Settings components
import Settings from './components/settings/Settings';
import StatusTypeSettings from './components/settings/StatusTypeSettings';
import DepartmentSettings from './components/settings/DepartmentSettings';
import GroupSettings from './components/settings/GroupSettings';
import TypeSettings from './components/settings/TypeSettings';
import ProjectFields from './components/settings/ProjectFields';
import IndustryTypes from './components/settings/IndustryTypes';
import VendorTypes from './components/settings/VendorTypes';
import ClientCustomFields from './components/settings/ClientCustomFields';
import AssociationTypes from './components/settings/AssociationTypes';

// Views components
import DueDateCalendar from './components/views/DueDateCalendar';
import InformedProjects from './components/views/InformedProjects';
import MissingInformation from './components/views/MissingInformation';
import MyWork from './components/views/MyWork';
import WorkloadReport from './components/views/WorkloadReport';

// Audit & Reporting components
import AuditReporting from './components/auditReporting/AuditReporting';
import DueDateChangeReport from './components/auditReporting/DueDateChangeReport';

// Client Portal components
import ClientPortal from './components/clientPortal/ClientPortal';
import ClientLogin from './components/clientPortal/ClientLogin';
import ClientDashboard from './components/clientPortal/ClientDashboard';

// Security components
import Security from './components/security/Security';
import Users from './components/security/Users';
import Roles from './components/security/Roles';
import Permissions from './components/security/Permissions';

// Price Book components
import PriceBookDashboard from './components/priceBook/PriceBookDashboard';
import CategoriesList from './components/priceBook/CategoriesList';
import CategoryForm from './components/priceBook/CategoryForm';
import ItemsList from './components/priceBook/ItemsList';
import ItemForm from './components/priceBook/ItemForm';
import UnitsList from './components/priceBook/UnitsList';
import UnitForm from './components/priceBook/UnitForm';
import FrequenciesList from './components/priceBook/FrequenciesList';
import FrequencyForm from './components/priceBook/FrequencyForm';
import TiersList from './components/priceBook/TiersList';
import TierForm from './components/priceBook/TierForm';
import ReportsList from './components/priceBook/ReportsList';
import ReportForm from './components/priceBook/ReportForm';
import ReportView from './components/priceBook/ReportView';
import ReportBuilder from './components/priceBook/ReportBuilder';

// Product Management components
import ProductsDashboard from './components/products/ProductsDashboard';
import ProductCategoriesList from './components/products/ProductCategoriesList';
import ProductCategoryForm from './components/products/ProductCategoryForm';
import ProductsList from './components/products/ProductsList';
import ProductForm from './components/products/ProductForm';
import ProductReports from './components/products/ProductReports';

// Data Import components
import DataImports from './components/imports/DataImports';

// Company Page components
import CompanyPage from './components/company/CompanyPage';
import CompanyFeed from './components/company/CompanyFeed';
import CompanyPosts from './components/company/CompanyPosts';
import CompanyEvents from './components/company/CompanyEvents';
import CompanyLeaderboard from './components/company/CompanyLeaderboard';
import RequiredReading from './components/company/RequiredReading';
import BookmarkedPosts from './components/company/BookmarkedPosts';
import Mentions from './components/company/Mentions';
import CompanyAdmin from './components/company/CompanyAdmin';
import CompanyCalendar from './components/company/CompanyCalendar';

// Profile components
import UserProfile from './components/profile/UserProfile';

// Add console logging to help debug route issues
console.log('App component loading, setting up routes...');

// Simple error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error("App crashed:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="bg-gray-800 p-8 rounded-lg shadow-md max-w-lg w-full border border-gray-700">
            <h1 className="text-red-400 text-2xl font-bold mb-4">Something went wrong</h1>
            <div className="bg-gray-700 p-4 rounded mb-4">
              <p className="text-red-300">{this.state.error?.toString()}</p>
            </div>
            <p className="mb-4 text-gray-300">Please try reloading the page. If the problem persists, contact support.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  console.log('Auth state:', { isAuthenticated, loading });

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <MenuPermissionsProvider>
            <div className="min-h-screen bg-gray-900">
              <Toaster 
                position="top-right" 
                toastOptions={{
                  // Default styling for all toasts
                  style: {
                    borderRadius: '8px',
                    background: '#363636',
                    color: '#fff',
                  },
                  // Custom styling for different toast types
                  success: {
                    style: {
                      background: '#10B981',
                    },
                    icon: '✓',
                  },
                  error: {
                    style: {
                      background: '#EF4444',
                    },
                    icon: '✕',
                  },
                }}
              />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                
                {/* Client Portal routes */}
                <Route path="/client-login" element={<ClientLogin />} />
                <Route path="/client-dashboard" element={<ClientDashboard />} />
                <Route path="/client-portal/:token" element={<ClientPortal />} />
                
                {/* Protected routes */}
                <Route path="/" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
                  <Route index element={<Navigate to="/company/feed" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="my-dashboard" element={<MyDashboard />} />
                  
                  {/* Client Routes */}
                  <Route path="clients" element={<ClientList />} />
                  <Route path="clients/add" element={<AddClient />} />
                  <Route path="clients/:id" element={<ClientDetails />} />
                  
                  {/* Vendor Routes */}
                  <Route path="vendors" element={<Vendors />} />
                  <Route path="vendors/add" element={<AddVendor />} />
                  <Route path="vendors/:id" element={<VendorDetail />} />
                  <Route path="vendors/:id/edit" element={<EditVendor />} />
                  
                  {/* Project Routes */}
                  <Route path="projects" element={<ProjectList />} />
                  <Route path="projects/add" element={<AddProject />} />
                  <Route path="projects/:id" element={<ProjectDetails />} />
                  
                  {/* Template Routes */}
                  <Route path="projects/templates/new" element={<TemplateForm />} />
                  <Route path="projects/templates/:id" element={<TemplateForm />} />
                  
                  {/* Phase Routes */}
                  <Route path="phases/:id" element={<PhaseDetails />} />
                  
                  {/* Step Routes */}
                  <Route path="steps/:id" element={<StepDetails />} />
                  
                  {/* My Work Route */}
                  <Route path="my-work" element={<MyWork />} />
                  
                  {/* Settings Routes */}
                  <Route path="settings" element={
                    <PermissionRoute menuPath="/settings">
                      <Settings />
                    </PermissionRoute>
                  }>
                    <Route index element={<Navigate to="status-types" replace />} />
                    <Route path="status-types" element={<StatusTypeSettings />} />
                    <Route path="departments" element={<DepartmentSettings />} />
                    <Route path="groups" element={<GroupSettings />} />
                    <Route path="types" element={<TypeSettings />} />
                    <Route path="project-fields" element={<ProjectFields />} />
                    <Route path="industry-types" element={<IndustryTypes />} />
                    <Route path="vendor-types" element={<VendorTypes />} />
                    <Route path="client-custom-fields" element={<ClientCustomFields />} />
                    <Route path="association-types" element={<AssociationTypes />} />
                  </Route>
                  
                  {/* Security Routes */}
                  <Route path="security" element={
                    <PermissionRoute menuPath="/security">
                      <Security />
                    </PermissionRoute>
                  }>
                    <Route index element={<Navigate to="users" replace />} />
                    <Route path="users" element={<Users />} />
                    <Route path="roles" element={<Roles />} />
                    <Route path="permissions" element={<Permissions />} />
                  </Route>
                  
                  {/* Price Book Routes */}
                  <Route path="price-book" element={<PriceBookDashboard />} />
                  <Route path="price-book/categories" element={<CategoriesList />} />
                  <Route path="price-book/categories/add" element={<CategoryForm />} />
                  <Route path="price-book/categories/:id/edit" element={<CategoryForm />} />
                  <Route path="price-book/items" element={<ItemsList />} />
                  <Route path="price-book/items/add" element={<ItemForm />} />
                  <Route path="price-book/items/:id/edit" element={<ItemForm />} />
                  <Route path="price-book/items/:itemId/tiers" element={<TiersList />} />
                  <Route path="price-book/items/:itemId/tiers/add" element={<TierForm />} />
                  <Route path="price-book/items/:itemId/tiers/:id/edit" element={<TierForm />} />
                  <Route path="price-book/units" element={<UnitsList />} />
                  <Route path="price-book/units/add" element={<UnitForm />} />
                  <Route path="price-book/units/:id/edit" element={<UnitForm />} />
                  <Route path="price-book/frequencies" element={<FrequenciesList />} />
                  <Route path="price-book/frequencies/add" element={<FrequencyForm />} />
                  <Route path="price-book/frequencies/:id/edit" element={<FrequencyForm />} />
                  <Route path="price-book/reports" element={<ReportsList />} />
                  <Route path="price-book/reports/add" element={<ReportForm />} />
                  <Route path="price-book/reports/:id" element={<ReportView />} />
                  <Route path="price-book/reports/:id/edit" element={<ReportForm />} />
                  <Route path="price-book/reports/:id/manage" element={<ReportBuilder />} />
                  
                  {/* Product Management Routes */}
                  <Route path="products" element={<ProductsDashboard />} />
                  <Route path="products/list" element={<ProductsList />} />
                  <Route path="products/add" element={<ProductForm />} />
                  <Route path="products/:id/edit" element={<ProductForm />} />
                  <Route path="products/categories" element={<ProductCategoriesList />} />
                  <Route path="products/categories/add" element={<ProductCategoryForm />} />
                  <Route path="products/categories/:id/edit" element={<ProductCategoryForm />} />
                  <Route path="products/reports" element={<ProductReports />} />
                  
                  {/* Views Routes - Direct redirect to due-date-calendar */}
                  <Route path="views" element={<Navigate to="/views/due-date-calendar" replace />} />
                  <Route path="views/due-date-calendar" element={<DueDateCalendar />} />
                  <Route path="views/informed-projects" element={<InformedProjects />} />
                  <Route path="views/missing-information" element={<MissingInformation />} />
                  <Route path="views/workload-report" element={<WorkloadReport />} />
                  
                  {/* Tasks Route */}
                  <Route path="tasks" element={<TasksPage />} />
                  
                  {/* Profile Route */}
                  <Route path="profile" element={<UserProfile />} />
                  
                  {/* Data Imports Route */}
                  <Route path="imports" element={
                    <PermissionRoute menuPath="/imports">
                      <DataImports />
                    </PermissionRoute>
                  } />
                  
                  {/* Company Page Routes */}
                  <Route path="company" element={
                    <PermissionRoute menuPath="/company">
                      <CompanyPage />
                    </PermissionRoute>
                  }>
                    <Route index element={<Navigate to="feed" replace />} />
                    <Route path="feed" element={<CompanyFeed />} />
                    <Route path="posts" element={<CompanyPosts />} />
                    <Route path="events" element={<CompanyEvents />} />
                    <Route path="leaderboard" element={<CompanyLeaderboard />} />
                    <Route path="required-reading" element={<RequiredReading />} />
                    <Route path="bookmarks" element={<BookmarkedPosts />} />
                    <Route path="mentions" element={<Mentions />} />
                    <Route path="admin" element={<CompanyAdmin />} />
                    <Route path="calendar" element={<CompanyCalendar />} />
                  </Route>
                  
                  {/* Audit & Reporting Routes */}
                  <Route path="audit-reporting" element={<AuditReporting />}>
                    <Route index element={<Navigate to="/audit-reporting/due-date-change-report" replace />} />
                    <Route path="due-date-change-report" element={<DueDateChangeReport />} />
                  </Route>
                </Route>
                
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Add route debugging */}
              <RouteDebugger />
            </div>
          </MenuPermissionsProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

// Helper component to log route changes
const RouteDebugger = () => {
  const location = useLocation();
  
  useEffect(() => {
    console.log('Current route:', location.pathname);
  }, [location]);
  
  return null;
};

export default App; 